import type { Dispatch, Middleware } from '@reduxjs/toolkit'
import { createAmplifyAuthClient } from 'services/auth/amplifyAuthClient'
import type {
    AuthClient,
    InAppCognitoConfig,
    NoPendingSignIn,
    SignInResult,
    Tokens,
} from 'services/auth/authClient'
import { windowBrowser, type Browser } from 'services/browser/browser'
import { ccpLogout, preLoginInitAppSuccess } from 'store/app/app.reducer'
import AppState from 'store/app/app.state'
import RootState from 'store/state'
import { authError, authenticateUser, mfaRequired } from 'store/user/user.actions'
import * as UserReducer from 'store/user/user.reducer'

import { isAnyAction } from 'utils'

function isCognito(identityManagement?: string) {
    return identityManagement === 'cognito'
}

const incorrectCredentials = 'Incorrect username or password'

const couldNotResetPassword =
    'Sorry, we could not reset your password. Did you enter the correct verification code?'

const logIn = (dispatch: Dispatch, tokens: Tokens) => {
    dispatch(authenticateUser(tokens.accessToken, true) as any)
}

const showSignInResult = (dispatch: Dispatch, result: SignInResult) => {
    switch (result.kind) {
        case 'signedIn':
            logIn(dispatch, result.tokens)
            return

        case 'mfaRequired':
            dispatch(mfaRequired({ type: result.medium, destination: result.destination }))
            return

        case 'newPasswordRequired':
            // Today: a challenge with required attributes fails the sign-in.
            if (result.requiredAttributes.length > 0) {
                dispatch(authError(incorrectCredentials))
                return
            }

            dispatch(UserReducer.newPasswordRequired())
            return

        case 'failed':
            // dont pass error message for security
            dispatch(authError(incorrectCredentials))
            return
    }
}

const showChallengeResult = (dispatch: Dispatch, result: SignInResult | NoPendingSignIn) => {
    if (result.kind === 'signedIn') {
        logIn(dispatch, result.tokens)
    } else if (result.kind === 'failed') {
        dispatch(authError(result.message))
    }

    // Today: noPendingSignIn is ignored, so the agent stays in the authenticating state.
}

type CognitoMiddlewareDependencies = {
    authClientFor: (config: InAppCognitoConfig) => AuthClient
    browser: Browser
}

export const createCognitoMiddleware = ({
    authClientFor,
    browser,
}: CognitoMiddlewareDependencies): Middleware<{}, RootState> => {
    const urlParams = new URLSearchParams(browser.readHash().replace('#', ''))
    const authClients = new Map<string, AuthClient>()

    const clientFor = (config: InAppCognitoConfig) => {
        const key = `${config.userPoolId}:${config.client_id}`
        const cachedClient = authClients.get(key)
        if (cachedClient) return cachedClient

        const client = authClientFor(config)
        authClients.set(key, client)
        return client
    }

    let passwordResetUsername: string | undefined

    return (store) => (next) => async (action) => {
        if (!isAnyAction(action)) return

        const { cognito, identityManagement } = store.getState().app

        switch (action.type) {
            case preLoginInitAppSuccess.type: {
                const { identityManagement, cognito } = action.payload as AppState
                if (!isCognito(identityManagement) || !cognito) return next(action)

                next(action)
                if (cognito.type === 'redirect') {
                    const token = urlParams.get('access_token')
                    if (token) {
                        store.dispatch(authenticateUser(token, true) as any)
                        browser.clearHash()
                    }
                } else if (cognito.type === 'in_app') {
                    clientFor(cognito)
                        .currentSession()
                        .then((tokens) => {
                            if (tokens) logIn(store.dispatch, tokens)
                        })
                        .catch((error) => {
                            console.error('Error during current session retrieval:', error)
                        })
                }
                return
            }
            case UserReducer.login.type: {
                const token = urlParams.get('access_token')
                if (isCognito(identityManagement) && cognito && !token) {
                    console.log('logging in as cognito')

                    if (
                        action.payload &&
                        cognito.type === 'in_app' &&
                        action.payload.password &&
                        action.payload.username
                    ) {
                        store.dispatch(UserReducer.authenticatingUser())

                        void clientFor(cognito)
                            .signIn(action.payload.username, action.payload.password)
                            .then((result) => showSignInResult(store.dispatch, result))
                    } else if (cognito.type === 'redirect') {
                        // Today: redirect_uri is not encoded, so a hash or query in the page URL breaks it.
                        browser.navigate(
                            `${cognito.domain}/login?response_type=token&client_id=${cognito.client_id}&redirect_uri=${browser.currentUrl()}&scope=openid+profile+aws.cognito.signin.user.admin`,
                        )
                    }
                }

                return next(action)
            }

            case UserReducer.forgotPasswordGetCode.type:
                if (identityManagement === 'cognito' && cognito && cognito.type === 'in_app') {
                    store.dispatch(UserReducer.authenticatingUser())

                    passwordResetUsername = action.payload
                    const moveToEnterCode = () => next(action)

                    // Today: a failed request moves to the enter-code step too, with no error.
                    clientFor(cognito)
                        .requestPasswordReset(action.payload)
                        .then(moveToEnterCode, moveToEnterCode)
                }

                return

            case UserReducer.forgotPasswordConfirm.type:
                if (identityManagement === 'cognito' && cognito && cognito.type === 'in_app') {
                    store.dispatch(UserReducer.authenticatingUser())

                    const { verificationCode, newPassword } = action.payload

                    const showResetSuccess = () => {
                        store.dispatch(UserReducer.forgotPasswordSuccess())
                        next(action)
                    }

                    const showCouldNotReset = () => store.dispatch(authError(couldNotResetPassword))

                    if (passwordResetUsername) {
                        clientFor(cognito)
                            .confirmPasswordReset(
                                passwordResetUsername,
                                verificationCode,
                                newPassword,
                            )
                            .then(showResetSuccess, showCouldNotReset)
                    } else {
                        showCouldNotReset()
                    }
                }

                return next(action)

            case UserReducer.newPasswordRequiredChange.type:
                if (identityManagement === 'cognito' && cognito && cognito.type === 'in_app') {
                    const { newPassword } = action.payload

                    store.dispatch(UserReducer.authenticatingUser())

                    void clientFor(cognito)
                        .completeNewPassword(newPassword)
                        .then((result) => showChallengeResult(store.dispatch, result))
                }

                return next(action)

            case UserReducer.submitMFA.type:
                if (identityManagement === 'cognito' && cognito && cognito.type === 'in_app') {
                    store.dispatch(UserReducer.authenticatingUser())

                    void clientFor(cognito)
                        .confirmMfa(action.payload)
                        .then((result) => showChallengeResult(store.dispatch, result))
                }

                return next(action)

            case ccpLogout.type:
            case UserReducer.logout.type:
                next(action)
                if (identityManagement === 'cognito' && cognito && cognito.type === 'redirect') {
                    // if using the redirect cognito integration open a new tab with the logout url needed to logout of cognito and ADFS
                    browser.openWindow(
                        `${cognito.domain}/logout?client_id=${cognito.client_id}&logout_uri=${cognito.logoutUrl}`,
                        '_blank',
                    )
                }

                if (isCognito(identityManagement) && cognito?.type === 'in_app') {
                    await clientFor(cognito)
                        .signOut()
                        .catch((error) =>
                            console.warn('Error signing out of Cognito on logout:', error),
                        )
                }

                return
            default:
                return next(action)
        }
    }
}

export default createCognitoMiddleware({
    authClientFor: createAmplifyAuthClient,
    browser: windowBrowser,
})
